import type { Store } from "@/common/storeUtils";
import type { WarmupEitherEditForm_TRIAL, WarmupWidgetProps } from "@newgenerated/shared/schema";
import { getTrialErrors, updateFormErrors, updateFormGeneralErrors, validate, type WarmupFormMeta } from "@/components/warmup/warmupForm";
import type { WarmupOptions } from "@/components/warmup/Warmup";
import { GaFormExperimental, type SubmissionState } from "@/components/form/GaForm";
import type { DeepReadonly } from "vue";
import type { GaVueComponent } from "@/common/vueUtils";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import { processWarmupEditForm } from "@newgenerated/shared/stubs";
import { GaFormFieldInputTextExperimental } from "@/components/form/GaFormFieldInputText";
import { GaFormFieldInputSelectExperimental } from "@/components/form/GaFormFieldInputSelect";
import { GaFormFieldInputCheckboxExperimental } from "@/components/form/GaFormFieldInputCheckbox";
import { GaButton } from "@/components/general/GaButton";

export function WarmupFormTrial(props: {
  editForm: Store<WarmupEitherEditForm_TRIAL>;
  metaForm: Store<WarmupFormMeta>;
  options: Store<WarmupOptions>;
  submissionState: Store<SubmissionState>;
  metaInfo: DeepReadonly<WarmupWidgetProps>;
}): GaVueComponent {
  const { t } = useGaContext();
  const changedFieldsStore = props.metaForm.sub("changedFields");

  const generalErrors = props.metaForm.sub("errors").sub("general").get();

  async function onSubmit(): Promise<void> {
    const response = await processWarmupEditForm({ body: { either: props.editForm.get() } });

    if (response.kind === "error") {
      updateFormGeneralErrors(props.metaForm, t);
      changedFieldsStore.set({ kind: "ALL" });
    } else {
      const processResult = response.data;

      updateFormErrors(props.metaForm, processResult);
      changedFieldsStore.set({ kind: "ALL" });

      if (processResult.kind === "SUCCESS") {
        window.location.replace(URL.canParse(props.metaInfo.relayType.relayState) ? props.metaInfo.relayType.relayState : new URL("/", location.origin));
        // Prevent the submit button going back to "IDLE" until the redirect has taken effect.
        return;
      }
    }
    props.submissionState.set("IDLE");
  }

  async function onBlur(field: keyof WarmupEitherEditForm_TRIAL): Promise<void> {
    const { kind, store } = changedFieldsStore.unpackUnion();
    if (kind !== "ALL") {
      store.sub("fields").set([...store.sub("fields").get(), field]);
    }
    const response = await validate(() => ({ either: props.editForm.get() }));
    if (response.state === "ERROR") {
      updateFormGeneralErrors(props.metaForm, t);
    } else if (response.state === "SUCCESS") {
      const validateResult = response.value;
      updateFormErrors(props.metaForm, validateResult);
    }
  }

  return (
    <GaFormExperimental
      store={props.submissionState}
      onSubmit={onSubmit}
      fields={
        <>
          <div class="ga-dynamic-grid" style="--ga-dynamic-grid--min-width: 17rem;">
            <GaFormFieldInputTextExperimental
              store={props.editForm.sub("email")}
              type="email"
              name="email"
              label={t("aspSubscribe:subscribe.form.email")}
              disabled={true}
              required={true}
              onBlur={() => onBlur("email")}
              errors={getTrialErrors(props.metaForm, "email")}
            />
            <GaFormFieldInputTextExperimental
              store={props.editForm.sub("password")}
              type="password"
              name="password"
              label={t("aspSubscribe:subscribe.form.password")}
              required={true}
              onBlur={() => onBlur("password")}
              errors={getTrialErrors(props.metaForm, "password")}
            />
          </div>
          <div class="ga-dynamic-grid mt-3" style="--ga-dynamic-grid--min-width: 17rem;">
            <GaFormFieldInputTextExperimental
              store={props.editForm.sub("firstName")}
              type="text"
              name="firstName"
              label={t("subscribe:address.firstName")}
              required={true}
              onBlur={() => onBlur("firstName")}
              errors={getTrialErrors(props.metaForm, "firstName")}
            />
            <GaFormFieldInputTextExperimental
              store={props.editForm.sub("lastName")}
              type="text"
              name="lastName"
              label={t("subscribe:address.lastName")}
              required={true}
              onBlur={() => onBlur("lastName")}
              errors={getTrialErrors(props.metaForm, "lastName")}
            />
          </div>
          <div class="ga-dynamic-grid mt-3" style="--ga-dynamic-grid--min-width: 17rem;">
            <GaFormFieldInputSelectExperimental
              store={props.editForm.sub("language")}
              options={props.options.get().languages}
              name="language"
              label={t("subscribe:address.language")}
              onBlur={() => onBlur("language")}
              errors={getTrialErrors(props.metaForm, "language")}
            />
            <GaFormFieldInputSelectExperimental
              store={props.editForm.sub("countryCode")}
              options={props.options.get().countries}
              name="country"
              label={t("subscribe:address.country")}
              onBlur={() => onBlur("countryCode")}
              errors={getTrialErrors(props.metaForm, "countryCode")}
            />
            <div class="mt-3">
              <GaFormFieldInputSelectExperimental
                store={props.editForm.sub("frequency")}
                options={props.options.get().frequencies}
                name="frequency"
                label={t("warmup2:form.frequency.label")}
                onBlur={() => onBlur("frequency")}
                errors={getTrialErrors(props.metaForm, "frequency")}
              />
            </div>
          </div>

          <div class="mt-5">
            <GaFormFieldInputCheckboxExperimental
              store={props.editForm.sub("termsAndConditions")}
              labelHtml={t("warmup2:form.termsAndConditions.label", ["/terms-of-use", "/privacy-policy/corporate"])}
              name="tac"
              onBlur={() => onBlur("termsAndConditions")}
              errors={getTrialErrors(props.metaForm, "termsAndConditions")}
            />
          </div>
        </>
      }
      footer={
        <div class="mt-4">
          {props.submissionState.get() === "IDLE" ? (
            <GaButton type="submit">{t("warmup2:button.label")}</GaButton>
          ) : (
            <button class="btn btn-primary" type="button" disabled>
              <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
              <span class="visually-hidden" role="status">
                Loading...
              </span>
            </button>
          )}
        </div>
      }
      errors={changedFieldsStore.get().kind === "ALL" ? generalErrors : []}
    />
  );
}
